import {Connection, IPublishOptions, ISubscribeOptions, ISubscription, Session, SubscribeHandler} from "autobahn";
import * as log from "@/modules/logs";

let _connection: Connection | null = null
let _onClose: (reason: string) => void
let _onOpen: () => void

const _subscriptions: ISubscription[] = []

function checkSession () {
    if (!_connection) {
        throw new Error('No existe ninguna sesión en el enrutador WAMP')
    }
}

/**
 * Comprueba si hay activa una conexión con el enrutador WAMP
 */
export function isConnected () {
    return !!_connection && !!_connection.session
}

/**
 * Inicia una nueva sesión en el enrutador WAMP
 */
export async function connect () {
    // Ya existe una sesión creada
    if (_connection) {
        return _connection
    }

    const url = process.env.VUE_APP_WAMP_ROUTER_URL
    const realm = process.env.VUE_APP_WAMP_REALM
    const authid = process.env.VUE_APP_WAMP_AUTHID
    const ticket = process.env.VUE_APP_WAMP_AUTH_TICKET || ''

    if (!url) {
        throw new Error('No se ha especificado la URL para el enrutador WAMP')
    }

    if (!realm) {
        throw new Error('No se ha especificado ningún reino WAMP al cual acceder')
    }

    if (!authid) {
        throw new Error('No se ha especificado ningún ID de autenticación')
    }

    // Crea una nueva conexión con el enrutador WAMP
    const connection = new Connection({
        url,
        realm,
        authmethods: ['ticket'],
        authid,
        onchallenge: () => ticket,
    });

    const promise = new Promise<Session>((resolve, reject) => {
        // Cuando la conexión es abierta la promesa es resulta con la nueva
        // sesión creada
        connection.onopen = (session, details) => {
            console.log('INFO', `Sesión iniciada en el enrutador WAMP con id ${session.id}`);
            console.log('INFO', `Autenticado con el método "${details.authmethod}" y el rol ${details.authrole}`);
            _connection = connection
            if (_onOpen) {
                _onOpen()
            }

            if (!_subscriptions.length) {
                return resolve(session)
            }

            const p = Promise.all(_subscriptions.map(sub => {
                log.info(`Re-enlazando la suscripción ${sub.topic}`)
                return session.subscribe(sub.topic, sub.handler, sub.options)
            }))

            p.then(() => resolve(session))
        };

        connection.onclose = (reason) => {
            reject(new Error(reason));

            console.warn('WARN', `La  conexión ha sido cerrada motivo: ${reason}`)

            if (_onClose) {
                _onClose(reason)
            }

            return false
        };
    });

    connection.open();

    return promise;
}

/**
 * Solicita al enrutador suscribirse a un nuevo evento
 *
 * @param topic
 * @param handler
 * @param options
 */
export async function subscribe (topic: string, handler: SubscribeHandler, options: ISubscribeOptions = {})  {
    checkSession()

    const s = await _connection!.session!.subscribe(topic, (a, b, c) => {
        console.log(`Evento recibido ${topic}`)
        handler(a, b, c)
    }, options)

    log.info(`[WAMP] Suscripción creada para el evento ${topic} con id ${s.id}`)

    _subscriptions.push(s)

    return s
}

/**
 * Publica en un evento
 *
 * @param topic
 * @param args
 * @param kwargs
 * @param options
 */
export function publish (topic: string, args?: any[], kwargs?: any, options?: IPublishOptions) {
    checkSession()

    console.log(`Evento emitido ${topic}`)

    return _connection!.session!.publish(topic, args, kwargs, options)
}

/**
 * Cierra la sesión actual
 */
export async function close () {
    return new Promise(resolve => {
        if (!_connection) {
            return resolve(false)
        }

        _connection.onclose = (reason: string) => {
            resolve(true)
            return true
        }

        _connection.close()
    })
}

export function onClose(handler: (reason: string) => void) {
    _onClose = handler
}

export function onOpen(handler: () => void) {
    _onOpen = handler
}


function findSubscription (topic: string) {
    if (!_connection || !_connection.session) {
        return null
    }

   return _subscriptions.find(s => s.topic === topic) || null
}


/**
 * Elimina una suscripción al evento
 *
 * @param topic
 */
export async function unsubscribe (topic: string) {
    if (!_connection || !_connection.session) {
        return
    }

    const session = _connection.session!
    const sub = findSubscription(topic)
    if (!sub) {
        return null
    }

    await session.unsubscribe(sub)
    const index = _subscriptions.findIndex(s => s.topic === topic)
    index !== -1 && _subscriptions.splice(index, 1)

    log.info(`[WAMP] La suscripción al evento ${topic} ha sido eliminada`)
}

/**
 * Elimina todas las suscripciones
 */
export function clear () {
    log.info('[WAMP] Limpiando lista de suscripciones')
    return Promise.all(
        _subscriptions.map(s => unsubscribe(s.topic))
    )
}
